import { BackspaceRounded, CancelRounded, KeyboardReturn, SearchRounded } from '@mui/icons-material'
import { Button, IconButton, InputAdornment, InputBase, Tooltip, styled } from '@mui/material'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { globalSearchQuery } from '../../../../redux/actions/global'
import useScreenWidth from '../../hooks/useScreenWidth'

export const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover, &:active, &:focus, &:focus-within': {
    color: theme.palette.grey[900],
    backgroundColor: theme.palette.grey[200],
    borderRadius: 8,
    fontWeight: 500,
    '& svg': {
      color: `${theme.palette.primary.main} !important`
    }
  },
  '&.small': {
    padding: theme.spacing(0, 1),
    transform: 'scale(0.8)',
    '& svg': {
      color: `${theme.palette.grey[300]} !important`
    }
  }
}))

export const ShortcutSearch = styled(Button)(({ theme }) => ({
  color: `${theme.palette.grey[400]} !important`,
  backgroundColor: theme.palette.grey[100],
  '& svg': {
    color: `${theme.palette.grey[400]} !important`
  },
  '&:hover, &:active, &:focus, &:focus-within': {
    color: `${theme.palette.grey[500]} !important`,
    backgroundColor: theme.palette.grey[200],
    boxShadow: 'none',
    '& svg': {
      color: `${theme.palette.grey[500]} !important`
    }
  }
}))

export const StyledSearchInput = styled(InputBase)(({ theme }) => ({
  '&.MuiInputBase-root': {
    color: theme.palette.grey[600],
    '&.client': {
      '&:hover, &:active, &:focus, &:focus-within': {
        color: theme.palette.grey[900],
        backgroundColor: theme.palette.grey[200],
        borderRadius: 8,
        fontWeight: 500,
        '& svg': {
          color: `${theme.palette.primary.main} !important`
        }
      }
    },
    '&.admin': {
      color: theme.palette.grey[100],
      backgroundColor: theme.palette.grey[600],
      border: 'none',
      borderRadius: 4,
      '& svg': {
        color: `${theme.palette.grey[300]} !important`
      },
      '&:hover, &:active, &:focus, &:focus-within': {
        color: theme.palette.grey[50],
        backgroundColor: theme.palette.grey[700],
        border: 'none',
        borderRadius: 4,
        '& svg': {
          color: `${theme.palette.secondary.main} !important`
        }
      }
    }
  },
  '.MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    '&.small': {
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 400,
      '&:focus': {
        fontWeight: 500
      }
    },
    '&.header': {
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create('width'),
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: '18ch',
        '&:focus': {
          width: '20ch'
        }
      },
      [theme.breakpoints.down('sm')]: {
        width: '0.1ch',
        '&:focus': {
          width: `calc(${window.innerWidth}px - 88px)`,
          height: 40
        }
      }
    }
  }
}))

function InputGlobalSearch({ handleClose }: any) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(960)
  const dispatch = useDispatch()
  const inputRef = useRef<string>('')

  const handleEnterKey = debounce((event: any) => {
    inputRef.current = event.target.value
    if (event.key === 'Enter') {
      handleOnSubmit(inputRef.current)
    } else {
      handleOnSubmit(inputRef.current)
    }
  }, 500)

  function handleOnSubmit(query: string) {
    dispatch(globalSearchQuery(query))
  }

  function handleOnReset() {
    handleOnSubmit('')
  }

  useEffect(() => {
    handleOnSubmit('')
  }, [])

  return (
    <StyledSearchInput
      autoFocus={true}
      fullWidth
      placeholder={`${t('searchIn')} ${import.meta.env.VITE_NAME}`}
      classes={{
        root: clsx(isMobile ? 'small' : ''),
        input: clsx(isMobile ? 'small' : 'header')
      }}
      id="search-focus"
      inputProps={{ 'aria-label': 'search', ref: inputRef }}
      startAdornment={
        <StyledInputAdornment position="start">
          <IconButton
            aria-label="toggle search submit"
            onClick={() => {
              handleOnSubmit(inputRef.current)
            }}
            edge="start"
          >
            <SearchRounded />
          </IconButton>
        </StyledInputAdornment>
      }
      endAdornment={
        <InputAdornment position="end" sx={{ margin: 0, position: 'absolute', right: 0, height: '100%' }}>
          <Tooltip title={`${t('clear-search')}`}>
            <IconButton
              sx={theme => ({
                '& svg': {
                  color: `${theme.palette.grey[300]} !important`
                }
              })}
              onClick={() => {
                handleOnReset()
              }}
            >
              <BackspaceRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <ShortcutSearch
            size="small"
            variant="contained"
            aria-label="toggle search submit"
            endIcon={<KeyboardReturn />}
            onClick={() => {
              handleOnSubmit(inputRef.current)
            }}
          >
            {`${t('search')}`}
          </ShortcutSearch>
          <Tooltip title={`${t('cancel')}`}>
            <IconButton onClick={handleClose}>
              <CancelRounded />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      }
      onKeyUp={handleEnterKey}
    />
  )
}

export default InputGlobalSearch
